import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DropTarget } from 'react-dnd';
import NoScrollBar from '~/Components/Global/NoScrollBar/NoScrollBar';
import FontWrapper from '~/Containers/Global/FontWrapper/FontWrapper';
import { allForDayAndType } from '~/Redux/Modules/Tasks';
import { DAY_CHECKLIST } from '~/Config/DragTypes';
import { CUSTOM, TYPES } from '~/Config/Checklists';
import TaskItem from './TaskItem';
import NewTask from './NewTask';

import './Tasks.scss';
import TaskListHeader from './TaskListHeader';

const taskTarget = {
  canDrop: () => true,
  drop: () => {},
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  dragItem: monitor.getItem(),
  monitor: monitor,
});

const TaskList = ({
  date,
  type,
  header,
  dragItem,
  monitor,
  editableHeader,
  isTasksSectionCollapsed,
  isInCollapsibleSection,
  disableTaskWrapping = false,
  goalTaskWrapping = false,
}) => {
  const tasks = useSelector(state => allForDayAndType(state, date, type));
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(null);
  const [sizeRef, setSizeRef] = useState(null);
  const [newItemRef, setNewItemRef] = useState(null);
  const collapseWhileDragging = useRef(false);
  const shouldExpand = expanded && !(dragItem && collapseWhileDragging.current);

  let style = {};
  if (disableTaskWrapping) {
    if (shouldExpand && width) {
      style.width = width;
      style.minWidth = '100%';
    }
  } else if (goalTaskWrapping) {
    if (shouldExpand && width) {
      style.width = Math.min(width, 250);
      style.maxWidth = '250px';
    }
  } else {
    style.minWidth = '100%';
  }

  let childSizeRefs = useRef([]);

  const expand = () => {
    if (disableTaskWrapping) {
      const width = Math.max(...childSizeRefs.current.map(ref => ref.offsetWidth)) + 120;
      const left = sizeRef?.getBoundingClientRect()?.left ?? 0;
      if (width > sizeRef.offsetWidth) {
        setExpanded(true);
        if (left + width > window.innerWidth) {
          console.log('expand goes off screen!!!');
          setWidth(window.innerWidth - left);
        } else {
          setWidth(width);
        }
      }
    } else if (goalTaskWrapping) {
      const width = Math.max(...childSizeRefs.current.map(ref => ref.offsetWidth)) + 120;
      const left = sizeRef?.getBoundingClientRect()?.left ?? 0;
      if (width > sizeRef.offsetWidth) {
        setExpanded(true);
        if (left + width > window.innerWidth) {
          console.log('expand goes off screen!!!');
          setWidth(window.innerWidth - left);
        } else {
          setWidth(width);
        }
      }
    } else if (!expanded) {
      setExpanded(true);
    }
  };

  const expandForChild = useRef(() => expanded && expand());

  const collapse = () => {
    if (sizeRef.current) sizeRef.current.scrollLeft = 0;
    setExpanded(false);
  };

  useEffect(() => {
    if (!dragItem) collapseWhileDragging.current = false;
    monitor.internalMonitor.subscribeToOffsetChange(() => {
      if (dragItem && expanded && sizeRef) {
        collapseWhileDragging.current = true;
        const offset = monitor.getClientOffset();
        const { x, left, width } = sizeRef.getBoundingClientRect();
        if (offset && (offset.x < (x || left) || offset.x > (x || left) + width)) collapse();
      }
    });
  }, [dragItem, monitor, expanded, sizeRef, collapse, collapseWhileDragging]);

  const handleClick = e => {
    if (e.target.tagName === 'A') window.open(e.target.href, '_blank');
    if (
      e.target.classList.contains('scroll-child') ||
      e.target.classList.contains('tasks-wrapper') ||
      e.target.classList.contains('task-header')
    ) {
      newItemRef?.quill?.focus();
    }
  };

  const addSizeRef = useRef(ref => {
    if (!ref) return;
    childSizeRefs.current = [...childSizeRefs.current, ref];
  });

  return (
    <div
      className={`tasks-wrapper ${dragItem ? 'is-dragging' : ''} ${shouldExpand ? 'expanded' : ''}`}
      style={style}
      ref={r => setSizeRef(r)}
      onMouseEnter={!isInCollapsibleSection || !isTasksSectionCollapsed ? expand : null}
      onMouseLeave={collapse}
      onClick={handleClick}
    >
      <h6 className="task-header">
        {type === CUSTOM ? (
          header
        ) : (
          <TaskListHeader
            heading={header}
            type={type}
            isSectionCollapsed={isTasksSectionCollapsed}
            editable={editableHeader}
          />
        )}
      </h6>
      <FontWrapper>
        <NoScrollBar>
          {tasks.map((item, key) => {
            return (
              <TaskItem
                key={key}
                task={item}
                prevTask={key > 0 ? tasks[key - 1] : null}
                nextTask={key < tasks.length - 1 ? tasks[key + 1] : null}
                lastTask={tasks[tasks.length - 1]}
                addSizeRef={addSizeRef.current}
                expand={expandForChild.current}
                disableTaskWrapping={disableTaskWrapping}
                goalTaskWrapping={goalTaskWrapping}
              />
            );
          })}
          <NewTask
            date={date}
            type={type}
            setNewItemRef={setNewItemRef}
            prevTask={tasks.length ? tasks[tasks.length - 1] : null}
            disableTaskWrapping={disableTaskWrapping}
            goalTaskWrapping={goalTaskWrapping}
          />
        </NoScrollBar>
      </FontWrapper>
    </div>
  );
};

TaskList.propTypes = {
  date: PropTypes.object.isRequired,
  type: PropTypes.oneOf(TYPES).isRequired,
  header: PropTypes.node,
  dragItem: PropTypes.object,
  isOver: PropTypes.bool,
  monitor: PropTypes.object.isRequired,
  editableHeader: PropTypes.bool,
  isTasksSectionCollapsed: PropTypes.bool,
  isInCollapsibleSection: PropTypes.bool,
  disableTaskWrapping: PropTypes.bool,
  goalTaskWrapping: PropTypes.bool,
};

export default TaskList |> DropTarget(DAY_CHECKLIST, taskTarget, dropCollect);
